import {
  type CoworkerAndTeamSelectionMode,
  type Team,
  type UserInfo,
} from "@repo/api";
import {
  Accordion,
  AccordionItem,
  FormField,
  InlineMessage,
  ListView,
  ListViewItem,
  peopleIcon,
  personIcon,
  warehouseIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  getAbbreviationIndex,
  hasItems,
  isDefined,
  maskEmail,
} from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import noop from "lodash/noop";

import { useSettings } from "../../settings/SettingsProvider";
import { CoWorkerName } from "../CoWorkerName";
import { ElementsContainer } from "../ElementsContainer";
import {
  type TeamMemberState,
  type TeamSelection,
  isTeamSelection,
} from "./reducer";
import { getSelectionModeText } from "./utils";

export const Assigned = ({
  assigned,
  assignedTeam,
  title,
  onClick,
  required,
}: {
  assigned: UserInfo[] | undefined;
  assignedTeam: Team | undefined;
  title?: string;
  onClick?: () => void;
  required?: boolean;
}) => {
  const { customerMode } = useSettings();
  let content;
  if (!isEmpty(assigned) && isDefined(assigned)) {
    if (assigned.length > 1) {
      const description = t("prompt.coworkers_selected", {
        number: assigned.length,
      });
      content = (
        <ListViewItem
          control={onClick ? "navigational" : undefined}
          leadingIcon={peopleIcon}
          title={title ?? description}
          description={title ? description : t("prompt.individuals_selected")}
        />
      );
    } else {
      const description = (
        <CoWorkerName
          email={assigned?.[0]?.emailId}
          textStyle={title ? undefined : "Heading.XS"}
        />
      );
      content = (
        <ListViewItem
          control={onClick ? "navigational" : undefined}
          leadingIcon={personIcon}
          title={title ?? description}
          description={
            title
              ? description
              : customerMode
                ? maskEmail(
                    assigned?.[0]?.emailId,
                    getAbbreviationIndex(
                      assigned?.[0]?.emailId,
                      assigned.map((a) => a.emailId)
                    )
                  )
                : assigned?.[0]?.emailId
          }
        />
      );
    }
  }

  if (assignedTeam) {
    const description = assignedTeam.teamName;
    content = (
      <ListViewItem
        control={onClick ? "navigational" : undefined}
        leadingIcon={warehouseIcon}
        title={title ?? description}
        description={`${title ? description : assignedTeam.teamName} (${assignedTeam.teamId})`}
      />
    );
  }

  if (!content) {
    content = (
      <ListViewItem
        control={onClick ? "navigational" : undefined}
        leadingIcon={peopleIcon}
        title={title}
        description={
          required ? (
            <FormField
              valid={false}
              shouldValidate
              validation={{
                msg: t("label.required_field"),
                id: "error",
                type: "error",
              }}
            />
          ) : undefined
        }
      />
    );
  }

  return (
    <ElementsContainer onClick={onClick}>
      <ListView id="tour-assigned-coworkers" data-testid="assigned-coworkers">
        {content}
      </ListView>
    </ElementsContainer>
  );
};

const renderCoworkerItem = (
  a: UserInfo | undefined,
  checked: boolean,
  allMembers: UserInfo[],
  onSelect?: (toggledMember: UserInfo) => void,
  mask?: boolean
) => {
  if (!a?.emailId) {
    return null;
  }

  return (
    <ListViewItem
      key={a.emailId}
      control="checkbox"
      leadingIcon={personIcon}
      title={
        <CoWorkerName
          email={a.emailId}
          textStyle="Heading.XS"
          emails={allMembers.map((u) => u.emailId)}
          defaultValue={a.emailId}
        />
      }
      description={
        mask
          ? maskEmail(
              a.emailId,
              getAbbreviationIndex(
                a.emailId,
                allMembers.map((u) => u.emailId)
              )
            )
          : a.emailId
      }
      controlProps={{
        value: a.emailId,
        checked,
        onChange: () => {
          onSelect?.(a);
        },
      }}
    />
  );
};

const TeamHeader = ({
  team,
  assignedToTeam,
  onClearTeam,
}: {
  team: Team;
  assignedToTeam?: boolean;
  onClearTeam?: () => void;
}) => (
  <ListView id={`assigned-team-list-${team.teamId}`} showDivider={false}>
    <ListViewItem
      key={team.teamId}
      control="checkbox"
      leadingIcon={warehouseIcon}
      title={team.teamName}
      description={assignedToTeam ? `${t("prompt.describe_only_team")}` : null}
      addOn={`${t("label.ID")}: ${team.teamId}`}
      controlProps={{
        value: String(team.teamId),
        checked: true,
        onChange: onClearTeam ?? noop,
      }}
    />
  </ListView>
);

export const AssignedList = ({
  selected,
  onSelect,
  onSelectAll,
  onClearTeam,
  selectionMode,
}: {
  selected: TeamSelection | UserInfo[] | undefined;
  selectionMode: CoworkerAndTeamSelectionMode;
  onSelect?: (toggledMember: UserInfo) => void;
  onSelectAll?: (selectAll: boolean) => void;
  onClearTeam?: () => void;
}) => {
  const { customerMode } = useSettings();

  const renderMembers = () => {
    // display team with checkbox
    // team members with accordion
    if (isTeamSelection(selected)) {
      const { team, members } = selected;
      const validMembers = members.filter((member): member is TeamMemberState =>
        Boolean(member?.emailId)
      );
      const selectedMembers = selected.assignedToTeam
        ? []
        : validMembers.filter((m) => m.selected);
      const allSelected = selectedMembers.length === validMembers.length;
      return (
        <div>
          <TeamHeader
            key="team-header"
            team={team}
            assignedToTeam={selected.assignedToTeam}
            onClearTeam={onClearTeam}
          />
          {!selected.assignedToTeam && validMembers.length > 0 && (
            <Accordion>
              <AccordionItem
                id={`assigned-members-${team.teamId}`}
                title={t("prompt.team_member_selected", {
                  number: selectedMembers.length,
                  totalNumber: validMembers.length,
                })}
              >
                <ListView id={`assigned-members-list-${team.teamId}`}>
                  {onSelectAll && (
                    <ListViewItem
                      control="checkbox"
                      leadingIcon={peopleIcon}
                      title={
                        allSelected
                          ? t("action.deselect_all")
                          : t("action.select_all")
                      }
                      controlProps={{
                        value: "select-all",
                        checked: allSelected,
                        onChange: () => onSelectAll(!allSelected),
                      }}
                    />
                  )}
                  {validMembers.map((m) =>
                    renderCoworkerItem(
                      m,
                      m.selected,
                      validMembers,
                      onSelect,
                      customerMode
                    )
                  )}
                </ListView>
              </AccordionItem>
            </Accordion>
          )}
        </div>
      );
    }

    if (Array.isArray(selected)) {
      const validSelected = selected.filter((member): member is UserInfo =>
        Boolean(member?.emailId)
      );

      if (!hasItems(validSelected)) {
        return null;
      }

      // display a single coworker with checkbox
      if (validSelected.length === 1) {
        return (
          <ListView
            id="tour-assigned-coworkers"
            data-testid="assigned-coworkers"
          >
            {renderCoworkerItem(
              validSelected[0],
              true,
              validSelected,
              onSelect,
              customerMode
            )}
          </ListView>
        );
      }

      // display multiple coworkers with accordion
      return (
        <Accordion>
          <AccordionItem
            id="assigned-coworkers-accordion"
            title={t("prompt.coworkers_selected", {
              number: validSelected.length,
            })}
            open
          >
            <ListView
              id="tour-assigned-coworkers"
              data-testid="assigned-coworkers"
            >
              {validSelected.map((a) =>
                renderCoworkerItem(
                  a,
                  true,
                  validSelected,
                  onSelect,
                  customerMode
                )
              )}
            </ListView>
          </AccordionItem>
        </Accordion>
      );
    }

    return null;
  };

  const content = renderMembers();

  return (
    <ElementsContainer>
      {content ?? (
        <InlineMessage
          title={getSelectionModeText(selectionMode)?.emptyStateText}
          variant="informative"
        />
      )}
    </ElementsContainer>
  );
};
